<script setup>
// Assistente de IA — onde o assistente está atuando, e onde saiu de cena.
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import assistenteIaApi from 'dashboard/api/assistenteIa';
import { useAssistenteIaApi } from '../composables/useAssistenteIaApi';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TabelaVazia from '../components/TabelaVazia.vue';

const { t } = useI18n();
const { accountId } = useAccount();
const { carregando, erro, chamar } = useAssistenteIaApi();

const atendimentos = ref([]);
const assistentes = ref([]);
const meta = ref({ count: 0, current_page: 1 });
const filtros = ref({ situacao: '', assistenteId: '', page: 1 });
const confirmacao = ref(null);

const carregar = async () => {
  const dados = await chamar(assistenteIaApi.atendimentos(filtros.value));
  if (!dados) return;

  atendimentos.value = dados.payload;
  meta.value = dados.meta;
};

const executar = async () => {
  const { atendimento, acao } = confirmacao.value;
  confirmacao.value = null;

  const resposta =
    acao === 'pausar'
      ? await chamar(assistenteIaApi.pausarAtendimento(atendimento.id))
      : await chamar(assistenteIaApi.retomarAtendimento(atendimento.id));

  if (resposta) carregar();
};

const linkDaConversa = atendimento =>
  `/app/accounts/${accountId.value}/conversations/${atendimento.conversa.display_id}`;

const frase = (grupo, valor) =>
  valor ? t(`ASSISTENTE_IA.ATENDIMENTOS.${grupo}.${valor.toUpperCase()}`) : '—';

watch(
  () => [filtros.value.situacao, filtros.value.assistenteId],
  () => {
    filtros.value.page = 1;
    carregar();
  }
);

onMounted(async () => {
  const lista = await chamar(assistenteIaApi.assistentes());
  if (lista) assistentes.value = lista.payload;
  carregar();
});
</script>

<template>
  <section class="flex flex-col gap-4">
    <Banner v-if="erro" color="ruby">{{ erro }}</Banner>

    <div>
      <h2 class="text-heading-2 text-n-slate-12">
        {{ t('ASSISTENTE_IA.ATENDIMENTOS.TITULO') }}
      </h2>
      <p class="mb-0 text-n-slate-11">
        {{ t('ASSISTENTE_IA.ATENDIMENTOS.DESCRICAO') }}
      </p>
    </div>

    <div class="flex flex-wrap gap-3">
      <select
        v-model="filtros.situacao"
        class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2"
      >
        <option value="">{{ t('ASSISTENTE_IA.ATENDIMENTOS.TODOS') }}</option>
        <option value="ativo">
          {{ t('ASSISTENTE_IA.ATENDIMENTOS.SITUACOES.ATIVO') }}
        </option>
        <option value="pausado">
          {{ t('ASSISTENTE_IA.ATENDIMENTOS.SITUACOES.PAUSADO') }}
        </option>
        <option value="encerrado">
          {{ t('ASSISTENTE_IA.ATENDIMENTOS.SITUACOES.ENCERRADO') }}
        </option>
      </select>

      <select
        v-model="filtros.assistenteId"
        class="px-3 py-2 text-sm border rounded-lg border-n-weak bg-n-alpha-black2"
      >
        <option value="">{{ t('ASSISTENTE_IA.ATENDIMENTOS.TODOS') }}</option>
        <option v-for="a in assistentes" :key="a.id" :value="a.id">{{ a.nome }}</option>
      </select>
    </div>

    <div v-if="carregando && !atendimentos.length" class="flex justify-center py-16">
      <Spinner />
    </div>

    <TabelaVazia
      v-else-if="!atendimentos.length"
      :mensagem="t('ASSISTENTE_IA.ATENDIMENTOS.VAZIO')"
    />

    <table v-else class="w-full text-sm text-left">
      <thead class="border-b text-n-slate-11 border-n-weak">
        <tr>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.ATENDIMENTOS.CONVERSA') }}</th>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.ATENDIMENTOS.CAIXA') }}</th>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.ATENDIMENTOS.SITUACAO') }}</th>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.ATENDIMENTOS.MOTIVO') }}</th>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.ATENDIMENTOS.RESPOSTAS') }}</th>
          <th class="py-2 font-medium">{{ t('ASSISTENTE_IA.ATENDIMENTOS.FALHAS') }}</th>
          <th class="py-2 font-medium text-right">
            {{ t('ASSISTENTE_IA.ASSISTENTES.ACOES') }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="atendimento in atendimentos"
          :key="atendimento.id"
          class="border-b border-n-weak"
        >
          <td class="py-3">
            <a
              :href="linkDaConversa(atendimento)"
              class="text-n-blue-11 hover:underline"
            >
              #{{ atendimento.conversa.display_id }}
            </a>
            <div class="text-n-slate-11">{{ atendimento.assistente.nome }}</div>
          </td>
          <td class="py-3 text-n-slate-11">{{ atendimento.conversa.inbox_nome }}</td>
          <td class="py-3">
            <span
              class="px-2 py-0.5 text-xs rounded-full"
              :class="{
                'bg-n-teal-3 text-n-teal-11': atendimento.situacao === 'ativo',
                'bg-n-amber-3 text-n-amber-11': atendimento.situacao === 'pausado',
                'bg-n-slate-3 text-n-slate-11': atendimento.situacao === 'encerrado',
              }"
            >
              {{ frase('SITUACOES', atendimento.situacao) }}
            </span>
          </td>
          <td class="py-3 text-n-slate-11">
            {{ frase('MOTIVOS', atendimento.motivo_pausa) }}
          </td>
          <td class="py-3 text-n-slate-11">{{ atendimento.respostas_enviadas }}</td>
          <td class="py-3 text-n-slate-11">{{ atendimento.falhas_seguidas }}</td>
          <td class="py-3">
            <div class="flex justify-end gap-2">
              <button
                v-if="atendimento.situacao === 'ativo'"
                class="text-n-amber-11 hover:underline"
                @click="confirmacao = { atendimento, acao: 'pausar' }"
              >
                {{ t('ASSISTENTE_IA.ATENDIMENTOS.PAUSAR') }}
              </button>
              <button
                v-else
                class="text-n-blue-11 hover:underline"
                @click="confirmacao = { atendimento, acao: 'retomar' }"
              >
                {{ t('ASSISTENTE_IA.ATENDIMENTOS.RETOMAR') }}
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-if="meta.count > atendimentos.length" class="flex items-center gap-2">
      <Button
        :label="t('ASSISTENTE_IA.COMUM.ANTERIOR')"
        variant="faded"
        color="slate"
        size="xs"
        :disabled="filtros.page <= 1"
        @click="
          filtros.page -= 1;
          carregar();
        "
      />
      <span class="text-sm text-n-slate-11">
        {{ t('ASSISTENTE_IA.COMUM.PAGINA') }} {{ meta.current_page }}
      </span>
      <Button
        :label="t('ASSISTENTE_IA.COMUM.PROXIMA')"
        variant="faded"
        color="slate"
        size="xs"
        @click="
          filtros.page += 1;
          carregar();
        "
      />
    </div>

    <div
      v-if="confirmacao"
      class="flex flex-col gap-2 p-4 border rounded-xl border-n-weak bg-n-solid-1"
    >
      <p class="mb-0 font-medium text-n-slate-12">
        {{
          confirmacao.acao === 'pausar'
            ? t('ASSISTENTE_IA.ATENDIMENTOS.PAUSAR_TITULO')
            : t('ASSISTENTE_IA.ATENDIMENTOS.RETOMAR_TITULO')
        }}
      </p>
      <p class="mb-0 text-n-slate-11">
        {{
          confirmacao.acao === 'pausar'
            ? t('ASSISTENTE_IA.ATENDIMENTOS.PAUSAR_TEXTO')
            : t('ASSISTENTE_IA.ATENDIMENTOS.RETOMAR_TEXTO')
        }}
      </p>
      <div class="flex gap-2">
        <Button
          :label="t('ASSISTENTE_IA.COMUM.CONFIRMAR')"
          size="sm"
          @click="executar"
        />
        <Button
          :label="t('ASSISTENTE_IA.COMUM.CANCELAR')"
          variant="faded"
          color="slate"
          size="sm"
          @click="confirmacao = null"
        />
      </div>
    </div>
  </section>
</template>
